import type { TenantActivityDto } from '@lib/dto/team.dto'
import { billingSettingSchema } from '@lib/enums/billing-setting.enum'
import { connectionEndReasonSchema, type ConnectionEndReason } from '@lib/enums/connection-end-reason.enum'
import { mcpAccessLevelSchema, type McpAccessLevel } from '@lib/enums/mcp-access-level.enum'
import { mcpOrgSettingSchema, type McpOrgSetting } from '@lib/enums/mcp-org-setting.enum'
import type { JsonValue } from '@lib/helpers/json'
import type { TenantProfileInput } from '@lib/input/tenant.input'
import { formatCents, formatPrice } from '@lib/helpers/money'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

function text(value: JsonValue | undefined): string | null {
  return typeof value === 'string' ? value : null
}

/** A stored amount in cents as "$20.00", or `fallback` when it's missing. */
function centsLabel(value: JsonValue | undefined, fallback: string): string {
  return typeof value === 'number' ? formatCents(value) : fallback
}

function roleLabel(value: JsonValue | undefined): string {
  const role = text(value)?.toLowerCase()
  return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'a role'
}

/** "live" / "test" from a stored mode (`LIVE`), for sentences. */
function modeLabel(value: JsonValue | undefined): string {
  return text(value)?.toLowerCase() ?? 'live'
}

function list(value: JsonValue | undefined): string[] {
  return Array.isArray(value) ? value.flatMap((item) => (typeof item === 'string' ? [item] : [])) : []
}

function keyLabel(p: TenantActivityDto['payload']): string {
  const name = text(p['name'])
  const prefix = text(p['prefix'])
  return [name ? `“${name}”` : null, prefix ? `(${prefix}…)` : null].filter(Boolean).join(' ') || 'a key'
}

function originsChange(p: TenantActivityDto['payload']): string {
  const added = list(p['added'])
  const removed = list(p['removed'])
  const parts = [
    added.length > 0 ? `allowed ${added.join(', ')}` : null,
    removed.length > 0 ? `removed ${removed.join(', ')}` : null,
  ].filter(Boolean)
  return `Changed ${modeLabel(p['mode'])} embed origins: ${parts.join('; ') || 'no change'}`
}

function allowlistLabel(p: TenantActivityDto['payload']): string {
  const value = text(p['value']) ?? 'an address'
  return text(p['kind']) === 'DOMAIN' ? `anyone @${value}` : value
}

const FIELD_LABELS = {
  name: 'name',
  displayName: 'display name',
  fromName: 'email sender name',
  replyTo: 'reply-to address',
  logoUrl: 'logo',
  accentColor: 'accent color',
  cornerRadius: 'corners',
  fontFamily: 'font',
  timezone: 'time zone',
  paperCopyFeeMicros: 'paper-copy fee',
} as const satisfies Record<keyof TenantProfileInput, string>

function isProfileField(key: string): key is keyof TenantProfileInput {
  return Object.hasOwn(FIELD_LABELS, key)
}

function profileFieldLabel(key: string): string {
  if (isProfileField(key)) return FIELD_LABELS[key]
  return key
}

/** Payload keys added when a change came through an AI app over MCP (not fields of the change). */
const VIA_KEYS = ['via', 'app', 'viaApp', 'link']

/** Kinds whose sentence already names the AI app, so "via {app}" would repeat it. */
const NAMES_THE_APP = new Set<TenantActivityDto['type']>(['mcp.connected', 'mcp.disconnected', 'mcp.confirmation_declined'])

/**
 * One sentence describing what happened, without the actor (shown in its own
 * column). A change made through an AI app ends "via {app}".
 */
export function describeActivity(event: TenantActivityDto): string {
  const sentence = describeChange(event)
  if (text(event.payload['via']) !== 'mcp' || NAMES_THE_APP.has(event.type)) return sentence
  // `viaApp` when the change names an app of its own (an Owner removing someone's connection).
  return `${sentence} via ${text(event.payload['viaApp']) ?? text(event.payload['app']) ?? 'an AI app'}`
}

function describeChange(event: TenantActivityDto): string {
  const p = event.payload
  switch (event.type) {
    case 'member.invited':
      return `Invited ${text(p['email']) ?? 'someone'} as ${roleLabel(p['role'])}`
    case 'member.invite_revoked':
      return `Revoked the invitation for ${text(p['email']) ?? 'someone'}`
    case 'member.joined':
      return `Joined as ${roleLabel(p['role'])}`
    case 'member.role_changed':
      return `Changed a member's role from ${roleLabel(p['from'])} to ${roleLabel(p['to'])}`
    case 'member.removed':
      return `Removed ${text(p['email']) ?? 'a member'}`
    case 'tenant.created':
      return 'Created the organization'
    case 'tenant.updated': {
      const fields = Object.keys(p)
        .filter((key) => !VIA_KEYS.includes(key))
        .map((key) => profileFieldLabel(key))
      return fields.length > 0 ? `Updated the ${fields.join(', ')}` : 'Updated the organization'
    }
    case 'tenant.suspended':
      return text(p['reason']) ? `Suspended the organization: ${text(p['reason']) ?? ''}` : 'Suspended the organization'
    case 'tenant.reactivated':
      return 'Reactivated the organization'
    case 'tenant.closed': {
      const reason = text(p['reason'])
      return reason ? `Closed the organization: ${reason}` : 'Closed the organization'
    }
    case 'tenant.reopened':
      return 'Reopened the organization'
    case 'tenant.purged':
      return "Deleted the closed organization's envelope files (the audit trail remains)"
    case 'staff.viewed':
      return 'Viewed the dashboard (read-only)'
    case 'api_key.created':
      return `Created ${modeLabel(p['mode'])} API key ${keyLabel(p)}`
    case 'api_key.revoked':
      return `Revoked ${modeLabel(p['mode'])} API key ${keyLabel(p)}`
    case 'webhook.url_changed': {
      const to = text(p['to'])
      return to
        ? `Set the ${modeLabel(p['mode'])} webhook URL to ${to}`
        : `Removed the ${modeLabel(p['mode'])} webhook URL`
    }
    case 'webhook.secret_rotated': {
      const until = text(p['previousValidUntil'])
      return until
        ? `Rotated the ${modeLabel(p['mode'])} webhook secret, keeping the old one until ${new Date(until).toLocaleString()}`
        : `Rotated the ${modeLabel(p['mode'])} webhook secret`
    }
    case 'webhook.previous_secret_retired':
      return `Retired the old ${modeLabel(p['mode'])} webhook secret`
    case 'embed_origins.changed':
      return originsChange(p)
    case 'allowlist.added':
      return `Asked to send test email to ${allowlistLabel(p)}`
    case 'allowlist.verified':
      return `Verified ${allowlistLabel(p)} for test email`
    case 'allowlist.lapsed':
      return `Paused test email to ${allowlistLabel(p)}: its DNS record was gone at the weekly check`
    case 'allowlist.removed':
      return `Stopped test email to ${allowlistLabel(p)}`
    case 'mcp.connected':
      return `Connected ${text(p['app']) ?? 'an AI app'} (${accessLabel(p['level'])})`
    case 'mcp.disconnected':
      return `${text(p['app']) ?? 'An AI app'} was disconnected${disconnectReason(p['reason'])}`
    case 'mcp.confirmation_declined': {
      const action = lowerFirst(text(p['action']) ?? 'take an action')
      const app = text(p['app']) ?? 'an AI app'
      return text(p['reason']) === 'timeout'
        ? `Didn't confirm in time when ${app} asked to ${action}, so nothing was done`
        : `Declined when ${app} asked to ${action}, so nothing was done`
    }
    case 'mcp.access_changed': {
      const app = text(p['app']) ?? 'an AI app'
      const person = text(p['person'])
      return text(p['to']) === 'removed'
        ? `Removed this organization from ${person ? `${person}'s ` : ''}${app} connection`
        : `Changed ${app}'s access to ${accessLabel(p['to'])}`
    }
    case 'mcp.setting_changed':
      return `Changed the MCP setting from ${settingLabel(p['from'])} to ${settingLabel(p['to'])}`
    case 'billing.credit_granted': {
      const granted = centsLabel(p['amountCents'], 'credit')
      return `Granted ${granted} of credit${text(p['reason']) ? `: ${text(p['reason']) ?? ''}` : ''}`
    }
    case 'billing.exemption_changed':
      return p['exempt'] === true ? 'Stopped billing this organization' : 'Started billing this organization'
    case 'billing.credit_purchased': {
      const amount = centsLabel(p['amountCents'], 'credit')
      return text(p['source']) === 'auto_reload' ? `Auto-reload added ${amount}` : `Bought ${amount} of credit`
    }
    case 'billing.settings_changed': {
      const from = text(p['from'])
      const to = text(p['to'])
      const setting = billingSettingSchema.safeParse(text(p['setting']))
      if (!setting.success) return 'Changed a billing setting'
      switch (setting.data) {
        case 'card':
          return `Saved a card for auto-reload${to ? `: ${to}` : ''}`
        case 'auto_reload':
          return from && to ? `Changed auto-reload from ${from} to ${to}` : 'Changed auto-reload'
        case 'low_balance_threshold':
          return from && to ? `Changed the low-balance alert from ${from} to ${to}` : 'Changed the low-balance alert'
      }
    }
    case 'billing.auto_reload_paused':
      return `Auto-reload paused: ${text(p['reason']) ?? 'the card charge failed'}`
    case 'billing.price_notice': {
      const from = typeof p['fromMicros'] === 'number' ? formatPrice(p['fromMicros']) : 'the old price'
      const to = typeof p['toMicros'] === 'number' ? formatPrice(p['toMicros']) : 'a new price'
      const at = text(p['effectiveAt'])
      return p['kind'] === 'increase'
        ? `Price per live envelope changes from ${from} to ${to}${at ? ` on ${new Date(at).toLocaleDateString()}` : ''}`
        : `Price per live envelope changed from ${from} to ${to}`
    }
    case 'billing.refunded': {
      const amount = centsLabel(p['amountCents'], 'credit')
      return p['closure'] === true
        ? `Refunded ${amount} of paid credit on closing`
        : `Refunded ${amount} of credit (${centsLabel(p['feeCents'], 'a')} fee)`
    }
  }
}

const SETTING_LABELS = {
  off: 'off',
  test_only: 'test only',
  live_read: 'test and live read-only',
  full: 'full',
} as const satisfies Record<McpOrgSetting, string>

function settingLabel(setting: JsonValue | undefined): string {
  const parsed = mcpOrgSettingSchema.safeParse(text(setting))
  if (!parsed.success) return 'unknown'
  return SETTING_LABELS[parsed.data]
}

function lowerFirst(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1)
}

const ACCESS_LABELS = {
  test_only: 'test only',
  live_read: 'test and live read-only',
  live: 'test and live',
} as const satisfies Record<McpAccessLevel, string>

function accessLabel(level: JsonValue | undefined): string {
  const parsed = mcpAccessLevelSchema.safeParse(text(level))
  if (!parsed.success) return 'test only'
  return ACCESS_LABELS[parsed.data]
}

const DISCONNECT_REASONS = {
  user_disconnected: '',
  staff_disconnected: ' by PixelSign staff',
  password_reset: ' after a password reset',
  account_removed: ' because the account was removed',
  refresh_reuse: ': an old sign-in token was reused, so PixelSign cut it off',
  code_reuse: ': a sign-in code was reused, so PixelSign cut it off',
  idle: ' after going unused',
  expired: ': its sign-in expired',
} as const satisfies Record<ConnectionEndReason, string>

function disconnectReason(reason: JsonValue | undefined): string {
  const parsed = connectionEndReasonSchema.safeParse(text(reason))
  if (!parsed.success) return ''
  return DISCONNECT_REASONS[parsed.data]
}

interface ActorCellProps {
  actor: TenantActivityDto['actor']
}

function ActorCell({ actor }: ActorCellProps) {
  if (actor.kind === 'staff') {
    return (
      <span className="inline-flex items-center gap-1.5">
        {actor.name}
        <Badge tone="info">Staff</Badge>
      </span>
    )
  }
  return <span>{actor.name ?? 'PixelSign'}</span>
}

interface ActivityTableProps {
  events: TenantActivityDto[]
}

/** The tenant activity log, newest first. The API already hides what this audience may not see. */
export function ActivityTable({ events }: ActivityTableProps) {
  if (events.length === 0) {
    return <p className="text-body text-muted-foreground">No activity yet.</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>When</TableHead>
          <TableHead>Who</TableHead>
          <TableHead>What</TableHead>
          <TableHead>IP</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.map((event) => (
          <TableRow key={event.id}>
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {new Date(event.createdAt).toLocaleString()}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              <ActorCell actor={event.actor} />
            </TableCell>
            <TableCell>{describeActivity(event)}</TableCell>
            <TableCell className="font-mono text-caption text-muted-foreground">{event.ip ?? '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
